/**
 * Manager Email - Gmail + SMTP/IMAP
 * Connect Gmail OAuth or any SMTP/IMAP email. Inbox, read, compose, AI features.
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Mail, Loader2, Link2, ArrowLeft, Send, Sparkles, Archive, Inbox, PenLine, RotateCcw, Paperclip, Settings, X, Plus, Check, Zap, AlertTriangle, Bell, FileText, Trash2, Users, Clock, MessageSquare, Tag, ChevronDown, ChevronUp, Eye, CheckCircle, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';
import { FormattedChatMessage } from '@/components/chat/ChatRenderer';
import { SkeletonEmailList } from '@/components/pwa/Skeleton';
import { haptic } from '@/components/pwa/animations';
// SwipeableEmailItem removed - using simple tap

type View = 'inbox' | 'read' | 'compose' | 'connect';
type Provider = 'gmail' | 'outlook' | 'smtp' | null;

// Common SMTP presets
const SMTP_PRESETS: Record<string, { imapHost: string; imapPort: number; smtpHost: string; smtpPort: number }> = {
  'gmail.com': { imapHost: 'imap.gmail.com', imapPort: 993, smtpHost: 'smtp.gmail.com', smtpPort: 587 },
  'outlook.com': { imapHost: 'outlook.office365.com', imapPort: 993, smtpHost: 'smtp.office365.com', smtpPort: 587 },
  'hotmail.com': { imapHost: 'outlook.office365.com', imapPort: 993, smtpHost: 'smtp.office365.com', smtpPort: 587 },
  'yahoo.com': { imapHost: 'imap.mail.yahoo.com', imapPort: 993, smtpHost: 'smtp.mail.yahoo.com', smtpPort: 587 },
};

const CATEGORY_STYLES: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  urgent: { label: 'Urgent', color: 'text-red-400', bg: 'bg-red-500/20', icon: AlertTriangle },
  action_required: { label: 'Action', color: 'text-amber-400', bg: 'bg-amber-500/20', icon: Bell },
  client_related: { label: 'Client', color: 'text-blue-400', bg: 'bg-blue-500/20', icon: Users },
  fyi: { label: 'FYI', color: 'text-zinc-400', bg: 'bg-zinc-500/20', icon: FileText },
  newsletter: { label: 'News', color: 'text-zinc-500', bg: 'bg-zinc-600/20', icon: Mail },
  spam: { label: 'Spam', color: 'text-zinc-600', bg: 'bg-zinc-700/20', icon: Trash2 },
};

export default function EmailPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<View>(searchParams.get('view') === 'connect' || searchParams.get('gmail_connected') ? 'connect' : 'inbox');
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '' });
  const [aiInstructions, setAiInstructions] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [emailLimit, setEmailLimit] = useState(20);
  const [signature, setSignature] = useState(() => localStorage.getItem('pa-email-signature') || '');
  const [showSignatureEditor, setShowSignatureEditor] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState<any>({});
  const [showPrefsEditor, setShowPrefsEditor] = useState(false);

  // Load preferences from server (syncs across devices)
  const { data: serverPrefs } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: async () => { const r = await apiClient.get('/preferences'); return r.data.data || {}; },
  });
  useEffect(() => {
    if (serverPrefs) {
      const prefs = serverPrefs.emailPrefs || {};
      setEmailPrefs(prefs);
      if (serverPrefs.emailSignature && !signature) setSignature(serverPrefs.emailSignature);
      // Load SMTP form from server if local is empty
      if (serverPrefs.smtpForm && !smtpForm.email) {
        setSmtpForm((prev: any) => ({ ...prev, ...serverPrefs.smtpForm, password: '' }));
      }
    }
  }, [serverPrefs]);

  const savePrefs = async (prefs: any) => {
    setEmailPrefs(prefs);
    try { await apiClient.put('/preferences', { emailPrefs: prefs }); } catch {}
  };
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [classifications, setClassifications] = useState<Map<string, any>>(new Map());
  const autoClassifyDone = useRef(false);
  const [smtpForm, setSmtpForm] = useState(() => {
    const defaults = { email: '', displayName: '', imapHost: '', imapPort: '993', smtpHost: '', smtpPort: '587', username: '', password: '', useTls: true };
    try {
      const saved = localStorage.getItem('pa-smtp-form');
      if (!saved) return defaults;
      const parsed = { ...JSON.parse(saved), password: '' };
      // Fix bad host values (e.g. email address in host field)
      if (parsed.imapHost?.includes('@')) parsed.imapHost = '';
      if (parsed.smtpHost?.includes('@')) parsed.smtpHost = '';
      // Auto-generate hosts from email domain if missing
      const domain = parsed.email?.split('@')[1]?.toLowerCase();
      if (domain && !parsed.imapHost) parsed.imapHost = SMTP_PRESETS[domain]?.imapHost || `mail.${domain}`;
      if (domain && !parsed.smtpHost) parsed.smtpHost = SMTP_PRESETS[domain]?.smtpHost || `mail.${domain}`;
      return parsed;
    } catch { return defaults; }
  });
  const [smtpError, setSmtpError] = useState('');
  const [smtpSaved, setSmtpSaved] = useState(false);
  const [smtpExpanded, setSmtpExpanded] = useState(false);

  const saveSmtpInfo = () => {
    const { password, ...rest } = smtpForm;
    localStorage.setItem('pa-smtp-form', JSON.stringify(rest));
    setSmtpSaved(true);
    setTimeout(() => setSmtpSaved(false), 2000);
  };

  // Check both connections
  const { data: gmailStatus } = useQuery({
    queryKey: ['gmail-status'],
    queryFn: async () => { try { const r = await apiClient.get('/email/google/status'); return r.data.data; } catch { return { connected: false }; } },
    retry: false,
  });
  const { data: outlookStatus } = useQuery({
    queryKey: ['outlook-status'],
    queryFn: async () => { try { const r = await apiClient.get('/email/outlook/status'); return r.data.data; } catch { return { connected: false }; } },
    retry: false,
  });
  const { data: smtpStatus } = useQuery({
    queryKey: ['smtp-status'],
    queryFn: async () => { try { const r = await apiClient.get('/email/smtp/status'); return r.data.data; } catch { return { connected: false }; } },
    retry: false,
  });

  const gmailConnected = gmailStatus?.connected === true;
  const outlookConnected = outlookStatus?.connected === true;
  const smtpConnected = smtpStatus?.connected === true;
  const hasAnyConnection = gmailConnected || outlookConnected || smtpConnected;

  // Determine active provider - prefer whichever is connected, or let user pick
  const [activeProvider, setActiveProvider] = useState<Provider>(null);
  const provider: Provider = activeProvider || (gmailConnected ? 'gmail' : outlookConnected ? 'outlook' : smtpConnected ? 'smtp' : null);
  const apiBase = provider === 'gmail' ? '/email/google' : provider === 'outlook' ? '/email/outlook' : '/email/smtp';

  // Fetch inbox
  const { data: inbox, isLoading: loadingInbox, refetch: refetchInbox } = useQuery({
    queryKey: ['email-inbox', provider, searchQuery, emailLimit],
    queryFn: async () => {
      if (!provider) return { messages: [] };
      const params = provider === 'gmail' ? `?q=${encodeURIComponent(searchQuery ? `in:inbox ${searchQuery}` : 'in:inbox')}&maxResults=${emailLimit}` : `?maxResults=${emailLimit}`;
      const r = await apiClient.get(`${apiBase}/messages${params}`);
      return r.data.data;
    },
    enabled: hasAnyConnection,
  });

  // Load cached classifications on mount
  const { data: cachedClassifications } = useQuery({
    queryKey: ['email-classifications', provider],
    queryFn: async () => {
      if (!provider) return [];
      const r = await apiClient.get(`${apiBase}/classifications`);
      return r.data.data || [];
    },
    enabled: hasAnyConnection,
  });

  // Merge cached classifications into map + pre-populate summaries
  useEffect(() => {
    if (cachedClassifications && cachedClassifications.length > 0) {
      setClassifications(prev => {
        const next = new Map(prev);
        for (const c of cachedClassifications) {
          next.set(c.message_id, {
            messageId: c.message_id,
            category: c.category,
            priority: c.priority,
            summary: c.summary,
            actionItems: c.action_items,
            clientMatch: c.client_match,
          });
        }
        return next;
      });
      // Pre-populate summaries from cached classifications so emails don't re-summarise
      setSummaries(prev => {
        const next = new Map(prev);
        for (const c of cachedClassifications) {
          if (c.summary && !next.has(c.message_id)) {
            // Try to parse as JSON (full structured summary), fall back to plain text
            let data: any;
            try {
              data = JSON.parse(c.summary);
            } catch {
              data = {
                summary: c.summary,
                category: c.category,
                action: c.action_items || null,
                urgency: c.priority >= 4 ? 'high' : c.priority >= 2 ? 'medium' : 'low',
              };
            }
            next.set(c.message_id, { loading: false, data });
          }
        }
        return next;
      });
    }
  }, [cachedClassifications]);

  // Auto-classify unread emails after inbox loads
  useEffect(() => {
    if (!inbox?.messages || autoClassifyDone.current || !hasAnyConnection || !provider) return;
    const unread = inbox.messages.filter((m: any) => m.isUnread && !classifications.has(m.id));
    if (unread.length === 0) { autoClassifyDone.current = true; return; }
    autoClassifyDone.current = true;
    apiClient.post(`${apiBase}/classify`, { maxResults: 50, preferences: emailPrefs, force: false })
      .then(r => {
        const newClassifications = r.data?.data?.classifications || [];
        if (newClassifications.length > 0) {
          setClassifications(prev => {
            const next = new Map(prev);
            for (const c of newClassifications) next.set(c.messageId, c);
            return next;
          });
        }
      })
      .catch(() => {});
  }, [inbox, hasAnyConnection, provider]);

  // Unread count
  const { data: unreadData } = useQuery({
    queryKey: ['email-unread', provider],
    queryFn: async () => { if (!provider) return { unreadCount: 0 }; const r = await apiClient.get(`${apiBase}/unread-count`); return r.data.data; },
    enabled: hasAnyConnection,
    refetchInterval: 60000,
  });

  // Per-message summaries (keyed by message ID)
  const [summaries, setSummaries] = useState<Map<string, { loading: boolean; data?: any }>>(new Map());

  const fetchSummary = async (messageId: string, emailData?: { from: string; subject: string; body: string; date?: string }, force?: boolean) => {
    // Skip if already loaded (unless force re-analyse)
    const existing = summaries.get(messageId);
    if (!force && existing?.data) return;
    if (!force && existing?.loading) return; // Already in progress

    setSummaries(prev => new Map(prev).set(messageId, { loading: true }));
    try {
      const payload: any = { messageId };
      if (emailData) {
        payload.from = emailData.from;
        payload.subject = emailData.subject;
        payload.emailBody = emailData.body;
        payload.date = emailData.date;
      }
      const prefs = emailPrefs;
      if (prefs.businessContext || prefs.categories || prefs.junkRules || prefs.prioritySenders) {
        payload.preferences = prefs;
      }
      const r = await apiClient.post(`${apiBase}/summarise`, payload);
      const s = r.data.data?.summary;
      const structured = typeof s === 'object' ? s : { summary: s };
      setSummaries(prev => new Map(prev).set(messageId, { loading: false, data: structured }));

      // Map AI category to CATEGORY_STYLES keys for inbox badge
      const categoryMap: Record<string, string> = {
        meeting: 'action_required', request: 'action_required', quote: 'action_required',
        booking: 'action_required', order: 'action_required', reschedule: 'action_required',
        cancellation: 'urgent', payment: 'action_required', invoice: 'action_required',
        compliance: 'urgent', urgent: 'urgent',
        client: 'client_related', internal: 'fyi', update: 'fyi', fyi: 'fyi',
        marketing: 'newsletter', newsletter: 'newsletter',
        junk: 'spam', spam: 'spam',
      };
      const mappedCategory = categoryMap[structured.category?.toLowerCase()] || structured.category || 'fyi';

      // Update classification in inbox list
      setClassifications(prev => {
        const next = new Map(prev);
        const old = next.get(messageId) || {};
        next.set(messageId, {
          ...old,
          messageId,
          category: mappedCategory,
          priority: structured.urgency === 'high' ? 5 : structured.urgency === 'medium' ? 3 : 1,
          summary: typeof structured.summary === 'string' ? structured.summary : '',
          actionItems: structured.action || '',
          clientMatch: old.clientMatch || '',
        });
        return next;
      });
    } catch {
      setSummaries(prev => new Map(prev).set(messageId, { loading: false, data: { summary: 'Could not summarise this email.' } }));
    }
  };

  // AI Draft Reply
  const draftReplyMutation = useMutation({
    mutationFn: async ({ messageId, tone, instructions }: { messageId: string; tone?: string; instructions?: string }) => {
      const r = await apiClient.post(`${apiBase}/draft-reply`, { messageId, tone, instructions }); return r.data.data;
    },
  });

  // AI Compose
  const aiComposeMutation = useMutation({
    mutationFn: async (instructions: string) => { const r = await apiClient.post(`${apiBase}/compose`, { instructions }); return r.data.data; },
  });

  // Send email
  const sendMutation = useMutation({
    mutationFn: async (params: { to: string; subject: string; body: string; replyToMessageId?: string; threadId?: string }) => {
      const r = await apiClient.post(`${apiBase}/send`, { ...params, signature }); return r.data.data;
    },
    onSuccess: () => { setView('inbox'); setComposeData({ to: '', subject: '', body: '' }); queryClient.invalidateQueries({ queryKey: ['email-inbox'] }); },
  });

  // AI Classify
  const classifyMutation = useMutation({
    mutationFn: async (force?: boolean) => {
      const r = await apiClient.post(`${apiBase}/classify`, { maxResults: 50, preferences: emailPrefs, force: !!force });
      return r.data.data;
    },
    onSuccess: (data) => {
      const newClassifications = data?.classifications || [];
      if (newClassifications.length > 0) {
        setClassifications(prev => {
          const next = new Map(prev);
          for (const c of newClassifications) next.set(c.messageId, c);
          return next;
        });
      }
    },
  });

  // Archive (Gmail only)
  const archiveMutation = useMutation({
    mutationFn: async (messageId: string) => { await apiClient.post('/email/google/archive', { messageId }); },
    onSuccess: () => { setView('inbox'); queryClient.invalidateQueries({ queryKey: ['email-inbox'] }); },
  });

  // Delete email
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showTodoPopup, setShowTodoPopup] = useState(false);
  const [todoAssignTo, setTodoAssignTo] = useState('');
  const [emailTodos, setEmailTodos] = useState<Map<string, string>>(new Map()); // messageId -> todoId

  // Team members for todo assignment
  const { data: teamMembers } = useQuery({
    queryKey: ['todo-users'],
    queryFn: async () => { const r = await apiClient.get('/todo/users'); return r.data.data || []; },
  });

  // Load existing email-linked todos to know which emails are in todo
  const { data: myTodosForEmails } = useQuery({
    queryKey: ['todo-my-emails'],
    queryFn: async () => { const r = await apiClient.get('/todo/my?status=all'); return r.data.data || []; },
    enabled: hasAnyConnection,
  });

  // Build email->todo map
  useEffect(() => {
    if (myTodosForEmails) {
      const map = new Map<string, string>();
      for (const t of myTodosForEmails) {
        if (t.source_type === 'email' && t.source_id && t.status !== 'deleted') {
          map.set(t.source_id, t.id);
        }
      }
      setEmailTodos(map);
    }
  }, [myTodosForEmails]);

  const createTodoFromEmail = useMutation({
    mutationFn: async (params: { title: string; description: string; assignedTo: string; sourceData: any; messageId: string; priority: string }) => {
      const r = await apiClient.post('/todo/create', {
        title: params.title,
        description: params.description,
        assignedTo: params.assignedTo || undefined,
        priority: params.priority,
        sourceType: 'email',
        sourceId: params.messageId,
        sourceData: params.sourceData,
      });
      return r.data.data;
    },
    onSuccess: (data: any) => {
      setShowTodoPopup(false); setTodoAssignTo('');
      if (data?.source_id) setEmailTodos(prev => new Map(prev).set(data.source_id, data.id));
      queryClient.invalidateQueries({ queryKey: ['todo-stats'] });
      queryClient.invalidateQueries({ queryKey: ['todo-my-emails'] });
    },
  });

  const removeTodoFromEmail = useMutation({
    mutationFn: async (messageId: string) => {
      const todoId = emailTodos.get(messageId);
      if (todoId) await apiClient.delete(`/todo/${todoId}`);
    },
    onSuccess: (_: any, messageId: string) => {
      setEmailTodos(prev => { const next = new Map(prev); next.delete(messageId); return next; });
      queryClient.invalidateQueries({ queryKey: ['todo-stats'] });
      queryClient.invalidateQueries({ queryKey: ['todo-my-emails'] });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async (messageId: string) => { await apiClient.post(`${apiBase}/delete`, { messageId }); },
    onSuccess: () => { setConfirmDelete(null); setView('inbox'); queryClient.invalidateQueries({ queryKey: ['email-inbox'] }); queryClient.invalidateQueries({ queryKey: ['email-unread'] }); },
  });

  // SMTP connect
  const smtpConnectMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.post('/email/smtp/connect', smtpForm);
      return r.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smtp-status'] }); setView('inbox'); setSmtpError('');
      // Save SMTP form details to server so they sync across devices
      const { password, ...rest } = smtpForm;
      try { apiClient.put('/preferences', { smtpForm: rest }); } catch {}
    },
    onError: (e: any) => setSmtpError(e?.response?.data?.error || e.message || 'Connection failed'),
  });

  // Disconnect Gmail
  const gmailDisconnectMutation = useMutation({
    mutationFn: async () => { await apiClient.delete('/email/google/disconnect'); },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['gmail-status'] }); queryClient.invalidateQueries({ queryKey: ['email-inbox'] }); setActiveProvider(null); },
  });

  // Disconnect SMTP
  const smtpDisconnectMutation = useMutation({
    mutationFn: async () => { await apiClient.delete('/email/smtp/disconnect'); },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['smtp-status'] }); queryClient.invalidateQueries({ queryKey: ['email-inbox'] }); setActiveProvider(null); },
  });

  // Gmail connect
  const handleGmailConnect = async () => {
    try { const r = await apiClient.get('/email/google/connect?returnUrl=/pa/email'); if (r.data?.data?.authUrl) window.location.href = r.data.data.authUrl; } catch {}
  };

  // Auto-fill SMTP presets based on email domain
  const handleEmailChange = (email: string) => {
    setSmtpForm(prev => {
      const domain = email.split('@')[1]?.toLowerCase();
      const preset = domain ? SMTP_PRESETS[domain] : null;
      // For unknown domains, default to mail.domain
      const defaults = preset || (domain ? { imapHost: `mail.${domain}`, imapPort: 993, smtpHost: `mail.${domain}`, smtpPort: 587 } : {});
      return { ...prev, email, username: email, ...defaults };
    });
  };

  const openMessage = async (msg: any) => {
    setSelectedMessage(msg);
    setView('read');
    setShowOriginal(false);
    draftReplyMutation.reset();

    // Fetch full message + auto-summarise
    let fullMsg = msg;
    try {
      const r = await apiClient.get(`${apiBase}/messages/${msg.id}`);
      const full = r.data.data;
      if (full) { fullMsg = full; setSelectedMessage(full); }
    } catch {}

    // Auto-summarise only if not already cached
    const cached = summaries.get(msg.id);
    if (!cached?.data && !cached?.loading) {
      const bodyText = (fullMsg.body || fullMsg.snippet || '').substring(0, 3000);
      fetchSummary(msg.id, { from: fullMsg.from, subject: fullMsg.subject, body: bodyText, date: fullMsg.date });
    }

    // Mark as read
    if (msg.isUnread) {
      try {
        if (provider === 'gmail') {
          await apiClient.post('/email/google/mark-read', { messageId: msg.id });
        }
        queryClient.invalidateQueries({ queryKey: ['email-inbox'] });
        queryClient.invalidateQueries({ queryKey: ['email-unread'] });
      } catch {}
    }
  };

  const formatDate = (dateStr: string) => {
    try { const d = new Date(dateStr); const now = new Date(); if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }); return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return dateStr; }
  };
  const extractName = (from: string) => { const m = from.match(/^"?([^"<]+)"?\s*</); return m ? m[1].trim() : from.split('@')[0]; };
  const getInitials = (name: string) => { const parts = name.split(/[\s.]+/).filter(Boolean); return parts.length > 1 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase(); };
  const getAvatarColor = (name: string) => { let h = 0; for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h); return `hsl(${Math.abs(h) % 360}, 55%, 45%)`; };

  // ==================== Connect View ====================
  // No email connected - show simple CTA
  if (!hasAnyConnection && view !== 'connect') {
    return (
      <div className="px-4 py-6 max-w-lg mx-auto text-center">
        <div className="py-16">
          <div className="w-16 h-16 rounded-full bg-[#5048e5]/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="h-7 w-7 text-[#7c75f0]" />
          </div>
          <h3 className="text-base font-medium mb-2">No Email Connected</h3>
          <p className="text-sm text-zinc-500 mb-6 px-4">Connect your Gmail or company email to read, send, and manage emails with AI.</p>
          <button onClick={() => setView('connect')} className="px-6 py-2.5 bg-[#5048e5] rounded-xl text-sm font-medium hover:bg-[#5f58e8] transition active:scale-95">
            Configure Email
          </button>
        </div>
      </div>
    );
  }

  if (view === 'connect') {
    return (
      <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
        <div className="flex items-center gap-2">
          {hasAnyConnection && <button onClick={() => setView('inbox')} className="p-2 text-zinc-500 hover:text-zinc-300"><ArrowLeft className="h-5 w-5" /></button>}
          <Mail className="h-5 w-5 text-[#7c75f0]" />
          <h2 className="text-lg font-medium">Connect Email</h2>
        </div>

        {/* Gmail OAuth */}
        <div className="bg-zinc-900/30 border border-white/[0.04] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Gmail / Google Workspace</h3>
              <p className="text-xs text-zinc-500">One-click connect via Google</p>
            </div>
            {gmailConnected ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-lg">{gmailStatus?.email}</span>
                <button onClick={() => gmailDisconnectMutation.mutate()} disabled={gmailDisconnectMutation.isPending} className="text-xs text-red-400 hover:text-red-300 transition">
                  {gmailDisconnectMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Disconnect'}
                </button>
              </div>
            ) : (
              <button onClick={handleGmailConnect} className="px-4 py-1.5 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition">Connect</button>
            )}
          </div>
        </div>

        {/* Outlook / Office 365 */}
        <div className="bg-zinc-900/30 border border-white/[0.04] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Outlook / Office 365</h3>
              <p className="text-xs text-zinc-500">Microsoft, university, corporate emails</p>
            </div>
            {outlookConnected ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-lg">{outlookStatus?.email}</span>
                <button onClick={async () => { await apiClient.delete('/email/outlook/disconnect'); queryClient.invalidateQueries({ queryKey: ['outlook-status'] }); setActiveProvider(null); }} className="text-xs text-red-400 hover:text-red-300 transition">Disconnect</button>
              </div>
            ) : (
              <button onClick={async () => { try { const r = await apiClient.get('/email/outlook/connect'); if (r.data?.data?.authUrl) window.location.href = r.data.data.authUrl; } catch {} }} className="px-4 py-1.5 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition">Connect</button>
            )}
          </div>
        </div>

        {/* SMTP/IMAP */}
        <div className="bg-zinc-900/30 border border-white/[0.04] rounded-xl p-4 space-y-3">
          <button onClick={() => setSmtpExpanded(!smtpExpanded)} className="w-full flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-sm font-medium">Company Email (SMTP/IMAP)</h3>
              <p className="text-xs text-zinc-500">Connect any email: info@, yourname@, etc.</p>
            </div>
            {smtpConnected ? (
              <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-lg shrink-0">{smtpStatus?.email}</span>
            ) : (
              smtpExpanded ? <ChevronUp className="h-4 w-4 text-zinc-500 shrink-0" /> : <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />
            )}
          </button>

          {smtpExpanded && (
            <div className="space-y-2">
              {smtpConnected && (
                <div className="flex items-center justify-between pb-1 border-b border-white/[0.06] mb-2">
                  <span className="text-xs text-green-400">Connected</span>
                  <button onClick={() => smtpDisconnectMutation.mutate()} disabled={smtpDisconnectMutation.isPending} className="text-xs text-red-400 hover:text-red-300 transition">
                    {smtpDisconnectMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Disconnect'}
                  </button>
                </div>
              )}
              <input placeholder="Email address" value={smtpForm.email} onChange={e => handleEmailChange(e.target.value)} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none" />
              <input placeholder="Display name (optional)" value={smtpForm.displayName} onChange={e => setSmtpForm(p => ({ ...p, displayName: e.target.value }))} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none" />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="IMAP Host" value={smtpForm.imapHost} onChange={e => setSmtpForm(p => ({ ...p, imapHost: e.target.value }))} className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-xs outline-none" />
                <input placeholder="IMAP Port" value={smtpForm.imapPort} onChange={e => setSmtpForm(p => ({ ...p, imapPort: e.target.value }))} className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-xs outline-none" />
                <input placeholder="SMTP Host" value={smtpForm.smtpHost} onChange={e => setSmtpForm(p => ({ ...p, smtpHost: e.target.value }))} className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-xs outline-none" />
                <input placeholder="SMTP Port" value={smtpForm.smtpPort} onChange={e => setSmtpForm(p => ({ ...p, smtpPort: e.target.value }))} className="bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-xs outline-none" />
              </div>
              <input placeholder="Username (usually email)" value={smtpForm.username} onChange={e => setSmtpForm(p => ({ ...p, username: e.target.value }))} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none" />
              <input type="password" placeholder="Password / App password" value={smtpForm.password} onChange={e => setSmtpForm(p => ({ ...p, password: e.target.value }))} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none" />
              {smtpError && <p className="text-xs text-red-400">{smtpError}</p>}
              <div className="flex gap-2">
                <button
                  onClick={saveSmtpInfo}
                  disabled={!smtpForm.email}
                  className="flex-1 py-2 bg-white/[0.06] border border-white/[0.08] rounded-lg text-sm text-zinc-300 hover:bg-white/[0.1] transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {smtpSaved ? <><Check className="h-4 w-4 text-green-400" /> Saved</> : 'Save Info'}
                </button>
                <button
                  onClick={() => smtpConnectMutation.mutate()}
                  disabled={smtpConnectMutation.isPending || !smtpForm.email || !smtpForm.password}
                  className="flex-1 py-2 bg-[#5048e5] rounded-lg text-sm font-medium hover:bg-[#5f58e8] transition disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {smtpConnectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Test & Connect
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Email Signature */}
        <div className="bg-zinc-900/30 border border-white/[0.04] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Email Signature</h3>
            <button onClick={() => setShowSignatureEditor(!showSignatureEditor)} className="text-xs text-[#7c75f0] hover:underline">
              {showSignatureEditor ? 'Close' : signature ? 'Edit' : 'Add'}
            </button>
          </div>
          {signature && !showSignatureEditor && (
            <div className="text-xs text-zinc-400 whitespace-pre-wrap border-t border-white/[0.04] pt-2">{signature}</div>
          )}
          {showSignatureEditor && (
            <div className="space-y-2">
              <textarea
                placeholder="Your email signature, e.g.&#10;John Smith&#10;Senior Accountant&#10;Data Corner&#10;Ph: 0412 345 678"
                value={signature}
                onChange={e => setSignature(e.target.value)}
                className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none resize-none"
                rows={4}
              />
              <button
                onClick={() => { localStorage.setItem('pa-email-signature', signature); setShowSignatureEditor(false); try { apiClient.put('/preferences', { emailSignature: signature }); } catch {} }}
                className="px-4 py-1.5 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition flex items-center gap-1"
              >
                <Check className="h-3 w-3" /> Save Signature
              </button>
            </div>
          )}
        </div>

        {/* Email AI Preferences */}
        <div className="bg-zinc-900/30 border border-white/[0.04] rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Email AI Preferences</h3>
            <button onClick={() => setShowPrefsEditor(!showPrefsEditor)} className="text-xs text-[#7c75f0] hover:underline">
              {showPrefsEditor ? 'Close' : emailPrefs.businessContext ? 'Edit' : 'Configure'}
            </button>
          </div>
          {!showPrefsEditor && emailPrefs.businessContext && (
            <p className="text-xs text-zinc-500 line-clamp-2">{emailPrefs.businessContext}</p>
          )}
          {showPrefsEditor && (
            <div className="space-y-3">
              {/* Business Context */}
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Business Description</label>
                <textarea
                  placeholder="Describe your business so AI understands the email context, e.g. 'We are an accounting firm specialising in small business tax, BAS, and compliance for Australian clients'"
                  value={emailPrefs.businessContext || ''}
                  onChange={e => setEmailPrefs({ ...emailPrefs, businessContext: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none resize-none"
                  rows={3}
                />
              </div>

              {/* Custom Categories */}
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Email Categories (comma separated)</label>
                <input
                  placeholder="Quote, Reschedule, Client, Order, Booking, Cancellation, Payment, Invoice, Compliance, Meeting"
                  value={emailPrefs.categories || ''}
                  onChange={e => setEmailPrefs({ ...emailPrefs, categories: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none"
                />
              </div>

              {/* Junk Detection Rules */}
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Auto-Junk Rules (topics to flag as junk)</label>
                <textarea
                  placeholder="Marketing, Web design, SEO services, Link building, Social media marketing, Newsletter subscriptions, Crypto, Loan offers"
                  value={emailPrefs.junkRules || ''}
                  onChange={e => setEmailPrefs({ ...emailPrefs, junkRules: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none resize-none"
                  rows={2}
                />
              </div>

              {/* Priority Senders */}
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Priority Senders (always mark as important)</label>
                <input
                  placeholder="ato.gov.au, client-name@email.com, boss@company.com"
                  value={emailPrefs.prioritySenders || ''}
                  onChange={e => setEmailPrefs({ ...emailPrefs, prioritySenders: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none"
                />
              </div>

              <button
                onClick={() => { savePrefs(emailPrefs); setShowPrefsEditor(false); }}
                className="px-4 py-1.5 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition flex items-center gap-1"
              >
                <Check className="h-3 w-3" /> Save Preferences
              </button>
            </div>
          )}
        </div>

        {/* Cloud Storage */}
        <div className="bg-zinc-900/30 border border-white/[0.04] rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-medium">Cloud Storage</h3>
          <p className="text-xs text-zinc-500">Connect to browse and sync files to client folders</p>

          <CloudStorageConnections />
        </div>
      </div>
    );
  }

  // ==================== Compose View ====================
  if (view === 'compose') {
    return (
      <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setView('inbox')} className="p-2 text-zinc-500 hover:text-zinc-300"><ArrowLeft className="h-5 w-5" /></button>
          <h2 className="text-base font-medium">New Email</h2>
        </div>
        <div className="bg-[#5048e5]/5 border border-[#5048e5]/20 rounded-xl p-3 space-y-2">
          <p className="text-xs font-medium text-[#7c75f0] flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Compose</p>
          <textarea placeholder="Describe what to write..." value={aiInstructions} onChange={e => setAiInstructions(e.target.value)} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none resize-none" rows={2} />
          <button onClick={() => { if (aiInstructions.trim()) aiComposeMutation.mutate(aiInstructions); }} disabled={aiComposeMutation.isPending || !aiInstructions.trim()} className="px-4 py-1.5 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition disabled:opacity-40 flex items-center gap-1">
            {aiComposeMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Generate
          </button>
          {aiComposeMutation.isSuccess && <button onClick={() => { const d = aiComposeMutation.data; setComposeData({ to: d.to || composeData.to, subject: d.subject || composeData.subject, body: d.body || composeData.body }); }} className="text-xs text-[#7c75f0] hover:underline">Use generated email</button>}
        </div>
        <input placeholder="To" value={composeData.to} onChange={e => setComposeData({ ...composeData, to: e.target.value })} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none" />
        <input placeholder="Subject" value={composeData.subject} onChange={e => setComposeData({ ...composeData, subject: e.target.value })} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none" />
        <textarea placeholder="Message body..." value={composeData.body} onChange={e => setComposeData({ ...composeData, body: e.target.value })} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none resize-none" rows={8} />
        <button onClick={() => { if (composeData.to && composeData.subject) sendMutation.mutate(composeData); }} disabled={sendMutation.isPending || !composeData.to || !composeData.subject} className="w-full py-2.5 bg-[#5048e5] rounded-xl text-sm font-medium hover:bg-[#5f58e8] transition disabled:opacity-40 flex items-center justify-center gap-2">
          {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
        </button>
        {sendMutation.isError && <p className="text-xs text-red-400">Failed to send. Try again.</p>}
      </div>
    );
  }

  // ==================== Read View ====================
  if (view === 'read' && selectedMessage) {
    const msg = selectedMessage;
    const msgSummary = summaries.get(msg.id);
    const s = msgSummary?.data; // structured summary object
    const isLoadingSummary = msgSummary?.loading && !s;
    const cachedClassification = classifications.get(msg.id);
    const urgencyColors: Record<string, string> = { high: 'text-red-400 bg-red-500/15 border-red-500/20', medium: 'text-amber-400 bg-amber-500/15 border-amber-500/20', low: 'text-green-400 bg-green-500/15 border-green-500/20' };
    const sentimentIcons: Record<string, string> = { positive: 'text-green-400', neutral: 'text-zinc-400', negative: 'text-red-400' };
    return (
      <div className="px-4 py-6 max-w-lg mx-auto space-y-4 animate-[slideUp_0.2s_ease-out]">
        {/* Header */}
        <div className="flex items-center gap-2">
          <button onClick={() => setView('inbox')} className="p-2 text-zinc-500 hover:text-zinc-300"><ArrowLeft className="h-5 w-5" /></button>
          <h2 className="text-base font-medium flex-1 truncate">{msg.subject}</h2>
        </div>

        {/* Sender info */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0" style={{ backgroundColor: getAvatarColor(extractName(msg.from)) }}>
            {getInitials(extractName(msg.from))}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{extractName(msg.from)}</p>
            <p className="text-xs text-zinc-500">{formatDate(msg.date)}{msg.hasAttachments ? ' - Has attachments' : ''}</p>
          </div>
          {cachedClassification && CATEGORY_STYLES[cachedClassification.category] && (() => {
            const cat = CATEGORY_STYLES[cachedClassification.category];
            const CatIcon = cat.icon;
            return <span className={cn('text-[10px] px-2 py-1 rounded-lg flex items-center gap-1', cat.bg, cat.color)}><CatIcon className="h-3 w-3" />{cat.label}</span>;
          })()}
        </div>

        {/* AI Analysis */}
        {isLoadingSummary ? (
          <div className="bg-zinc-900/40 border border-white/[0.06] rounded-xl p-5 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-[#7c75f0]" />
            <span className="text-sm text-zinc-400">Reading and analysing email...</span>
          </div>
        ) : s ? (
          <div className="space-y-3">
            {/* Urgency + Category + Sentiment tags + Re-analyse */}
            <div className="flex gap-2 flex-wrap items-center">
              {s.urgency && (
                <span className={cn('text-xs px-2.5 py-1 rounded-lg border font-medium capitalize', urgencyColors[s.urgency] || urgencyColors.low)}>
                  {s.urgency === 'high' ? 'Urgent' : s.urgency === 'medium' ? 'Medium Priority' : 'Low Priority'}
                </span>
              )}
              {s.category && (
                <span className="text-xs px-2.5 py-1 rounded-lg bg-[#5048e5]/10 border border-[#5048e5]/20 text-[#7c75f0] capitalize flex items-center gap-1">
                  <Tag className="h-3 w-3" />{s.category}
                </span>
              )}
              {s.sentiment && s.sentiment !== 'neutral' && (
                <span className={cn('text-xs px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/[0.06] capitalize', sentimentIcons[s.sentiment])}>
                  {s.sentiment}
                </span>
              )}
              <button
                onClick={() => {
                  const bodyText = (selectedMessage.body || selectedMessage.snippet || '').substring(0, 3000);
                  fetchSummary(msg.id, { from: msg.from, subject: msg.subject, body: bodyText, date: msg.date }, true);
                }}
                disabled={msgSummary?.loading}
                className="ml-auto text-xs px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/[0.06] text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.08] transition flex items-center gap-1"
              >
                {msgSummary?.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                Re-analyse
              </button>
            </div>

            {/* Summary */}
            <div className="bg-zinc-900/40 border border-white/[0.06] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-7 h-7 rounded-lg bg-[#5048e5]/15 flex items-center justify-center"><Sparkles className="h-3.5 w-3.5 text-[#7c75f0]" /></div>
                <span className="text-sm font-medium text-[#7c75f0]">Summary</span>
              </div>
              <p className="text-[15px] text-zinc-200 leading-relaxed">{typeof s.summary === 'string' ? s.summary : JSON.stringify(s.summary)}</p>
            </div>

            {/* Key Points */}
            {s.keyPoints && s.keyPoints.length > 0 && (
              <div className="bg-zinc-900/40 border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-7 h-7 rounded-lg bg-cyan-500/15 flex items-center justify-center"><MessageSquare className="h-3.5 w-3.5 text-cyan-400" /></div>
                  <span className="text-sm font-medium text-cyan-400">Key Points</span>
                </div>
                <ul className="space-y-2">
                  {s.keyPoints.map((p: string, i: number) => (
                    <li key={i} className="flex items-start gap-2 text-[15px] text-zinc-200 leading-relaxed">
                      <span className="text-cyan-400 mt-1 shrink-0">-</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Needed + ToDo */}
            {s.action && s.action.toLowerCase() !== 'no action needed' && s.action.toLowerCase() !== 'none' && (() => {
              const isInTodo = emailTodos.has(msg.id);
              return (
              <div className={cn('rounded-xl p-4', isInTodo ? 'bg-green-500/5 border border-green-500/15' : 'bg-amber-500/5 border border-amber-500/15')}>
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', isInTodo ? 'bg-green-500/15' : 'bg-amber-500/15')}>
                      {isInTodo ? <CheckCircle className="h-3.5 w-3.5 text-green-400" /> : <Bell className="h-3.5 w-3.5 text-amber-400" />}
                    </div>
                    <span className={cn('text-sm font-medium', isInTodo ? 'text-green-400' : 'text-amber-400')}>Action Needed</span>
                    {isInTodo && <span className="text-[10px] px-2 py-0.5 rounded-md bg-green-500/20 text-green-400 font-medium">In Todo</span>}
                  </div>
                  {isInTodo ? (
                    <button
                      onClick={() => removeTodoFromEmail.mutate(msg.id)}
                      disabled={removeTodoFromEmail.isPending}
                      className="px-2.5 py-1 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400 hover:bg-red-500/20 transition flex items-center gap-1 active:scale-95"
                    >
                      {removeTodoFromEmail.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />} Remove
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowTodoPopup(!showTodoPopup)}
                      className="px-2.5 py-1 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition flex items-center gap-1 active:scale-95"
                    >
                      <Plus className="h-3 w-3" /> To Do
                    </button>
                  )}
                </div>
                <p className="text-[15px] text-zinc-200 leading-relaxed">{s.action}</p>

                {/* Assign Todo Popup */}
                {showTodoPopup && !isInTodo && (
                  <div className="mt-3 pt-3 border-t border-amber-500/15 space-y-2 animate-[slideUp_0.15s_ease-out]">
                    <p className="text-xs text-zinc-400">Assign this action to:</p>
                    <select
                      value={todoAssignTo}
                      onChange={e => setTodoAssignTo(e.target.value)}
                      className="w-full bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none text-zinc-200 [&>option]:bg-[#1a1030] [&>option]:text-zinc-200"
                    >
                      <option value="">My To Do List</option>
                      {(teamMembers || []).map((m: any) => (
                        <option key={m.id} value={m.id}>{m.first_name || m.email}{m.role ? ` (${m.role})` : ''}</option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={() => createTodoFromEmail.mutate({
                          title: `${msg.subject} - ${s.action}`,
                          description: `From: ${msg.from}\nAction: ${s.action}${s.deadline ? `\nDeadline: ${s.deadline}` : ''}${s.summary ? `\nContext: ${typeof s.summary === 'string' ? s.summary : ''}` : ''}`,
                          assignedTo: todoAssignTo,
                          priority: s.urgency === 'high' ? 'high' : s.urgency === 'medium' ? 'medium' : 'low',
                          messageId: msg.id,
                          sourceData: {
                            from: msg.from, subject: msg.subject, date: msg.date,
                            summary: s.summary, keyPoints: s.keyPoints, action: s.action,
                            deadline: s.deadline, urgency: s.urgency, sentiment: s.sentiment, category: s.category,
                          },
                        })}
                        disabled={createTodoFromEmail.isPending}
                        className="flex-1 py-2 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition disabled:opacity-40 flex items-center justify-center gap-1"
                      >
                        {createTodoFromEmail.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                        {todoAssignTo ? 'Assign Task' : 'Add to My Tasks'}
                      </button>
                      <button onClick={() => setShowTodoPopup(false)} className="px-3 py-2 bg-white/[0.06] rounded-lg text-xs text-zinc-400">Cancel</button>
                    </div>
                    {createTodoFromEmail.isSuccess && <p className="text-xs text-green-400">Task created!</p>}
                  </div>
                )}
              </div>
            ); })()}

            {/* Deadline */}
            {s.deadline && s.deadline !== 'null' && s.deadline !== null && (
              <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-red-500/15 flex items-center justify-center"><Clock className="h-3.5 w-3.5 text-red-400" /></div>
                  <span className="text-sm font-medium text-red-400">Deadline</span>
                  <span className="text-[15px] text-zinc-200 ml-1">{s.deadline}</span>
                </div>
              </div>
            )}

            {/* Client Match (from classification) */}
            {cachedClassification?.clientMatch && (
              <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center"><Users className="h-3.5 w-3.5 text-blue-400" /></div>
                  <span className="text-sm font-medium text-blue-400">Related Client</span>
                  <span className="text-[15px] text-zinc-200 ml-1">{cachedClassification.clientMatch}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-zinc-900/40 border border-white/[0.06] rounded-xl p-4 text-sm text-zinc-500">Could not summarise this email.</div>
        )}

        {/* View Original Email */}
        <button
          onClick={() => setShowOriginal(!showOriginal)}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-sm text-zinc-500 hover:text-zinc-300 transition"
        >
          <Eye className="h-4 w-4" />
          {showOriginal ? 'Hide Original Email' : 'View Original Email'}
          {showOriginal ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {showOriginal && (
          <div className="bg-zinc-900/30 border border-white/[0.06] rounded-xl p-4 text-[14px] text-zinc-300 whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">
            {(() => {
              const raw = msg.body || msg.bodyHtml || '';
              return raw
                .replace(/<style[\s\S]*?<\/style>/gi, '')
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/p>/gi, '\n')
                .replace(/<\/div>/gi, '\n')
                .replace(/<\/tr>/gi, '\n')
                .replace(/<\/li>/gi, '\n')
                .replace(/<[^>]+>/g, '')
                .replace(/&nbsp;/g, ' ')
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#(\d+);/g, (_: any, n: string) => String.fromCharCode(parseInt(n)))
                .replace(/\[cid:[^\]]+\]/g, '')
                .replace(/\n{3,}/g, '\n\n')
                .trim() || 'No content available';
            })()}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => draftReplyMutation.mutate({ messageId: msg.id })} disabled={draftReplyMutation.isPending} className="px-3 py-2 bg-[#5048e5]/10 border border-[#5048e5]/20 rounded-lg text-sm text-[#7c75f0] flex items-center gap-1.5 hover:bg-[#5048e5]/20 transition disabled:opacity-40">
            {draftReplyMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PenLine className="h-3.5 w-3.5" />} AI Reply
          </button>
          {provider === 'gmail' && (
            <button onClick={() => archiveMutation.mutate(msg.id)} disabled={archiveMutation.isPending} className="px-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-sm text-zinc-400 flex items-center gap-1.5 hover:bg-white/[0.08] transition">
              <Archive className="h-3.5 w-3.5" /> Archive
            </button>
          )}
          {confirmDelete === msg.id ? (
            <>
              <button onClick={() => deleteMutation.mutate(msg.id)} disabled={deleteMutation.isPending} className="px-3 py-2 bg-red-500 rounded-lg text-sm text-white font-medium flex items-center gap-1.5 active:scale-95 transition">
                {deleteMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Confirm Delete
              </button>
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-2 bg-white/[0.06] rounded-lg text-sm text-zinc-400 active:scale-95 transition">Cancel</button>
            </>
          ) : (
            <button onClick={() => setConfirmDelete(msg.id)} className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400 flex items-center gap-1.5 hover:bg-red-500/20 transition">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>

        {/* AI Draft Reply */}
        {draftReplyMutation.isSuccess && (
          <div className="bg-[#5048e5]/5 border border-[#5048e5]/20 rounded-xl p-4 space-y-3">
            <p className="text-sm font-medium text-[#7c75f0] mb-2 flex items-center gap-1.5"><PenLine className="h-4 w-4" /> Draft Reply</p>
            <div className="text-base text-zinc-200 leading-relaxed">
              <FormattedChatMessage content={draftReplyMutation.data?.draft} darkTheme />
            </div>
            <button onClick={() => { const d = draftReplyMutation.data; setComposeData({ to: d.to || msg.from, subject: d.subject || `Re: ${msg.subject}`, body: d.draft || '' }); setView('compose'); }} className="px-4 py-2 bg-[#5048e5] rounded-lg text-sm font-medium hover:bg-[#5f58e8] transition flex items-center gap-1.5"><Send className="h-4 w-4" /> Edit & Send</button>
          </div>
        )}

        {/* Quick Reply */}
        <div className="bg-zinc-900/30 border border-white/[0.04] rounded-xl p-3">
          <div className="flex gap-2">
            <input
              placeholder="Quick reply..."
              value={composeData.body}
              onChange={e => setComposeData({ ...composeData, to: msg.from, subject: `Re: ${msg.subject}`, body: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter' && composeData.body.trim()) { sendMutation.mutate({ to: msg.from, subject: `Re: ${msg.subject}`, body: composeData.body, threadId: msg.threadId }); } }}
              className="flex-1 bg-white/[0.06] border border-white/[0.08] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#5048e5]/40"
            />
            <button
              onClick={() => { if (composeData.body.trim()) sendMutation.mutate({ to: msg.from, subject: `Re: ${msg.subject}`, body: composeData.body, threadId: msg.threadId }); }}
              disabled={sendMutation.isPending || !composeData.body.trim()}
              className="px-3 py-2 bg-[#5048e5] rounded-lg hover:bg-[#5f58e8] transition disabled:opacity-40"
            >
              {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin text-white" /> : <Send className="h-4 w-4 text-white" />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== Inbox View ====================
  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="h-5 w-5 text-[#7c75f0]" />
        <h2 className="text-lg font-medium">Email</h2>
        {(unreadData?.unreadCount || 0) > 0 && <span className="text-xs bg-[#5048e5] text-white rounded-full px-2 py-0.5">{unreadData.unreadCount}</span>}
        <div className="ml-auto flex gap-2">
          <button onClick={() => refetchInbox()} className="p-1.5 text-zinc-500 hover:text-zinc-300"><RotateCcw className="h-4 w-4" /></button>
          <button onClick={() => { setComposeData({ to: '', subject: '', body: '' }); setView('compose'); }} className="p-1.5 text-[#7c75f0] hover:text-[#5f58e8]"><PenLine className="h-4 w-4" /></button>
          <button onClick={() => setView('connect')} className="p-1.5 text-zinc-500 hover:text-zinc-300"><Settings className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Provider Switcher (if multiple connected) */}
      {[gmailConnected, outlookConnected, smtpConnected].filter(Boolean).length > 1 && (
        <div className="flex gap-1 bg-white/[0.04] rounded-xl p-1">
          {gmailConnected && <button onClick={() => setActiveProvider('gmail')} className={cn('flex-1 text-xs py-1.5 rounded-lg font-medium transition', provider === 'gmail' ? 'bg-[#5048e5] text-white' : 'text-zinc-500')}>Gmail</button>}
          {outlookConnected && <button onClick={() => setActiveProvider('outlook')} className={cn('flex-1 text-xs py-1.5 rounded-lg font-medium transition', provider === 'outlook' ? 'bg-[#5048e5] text-white' : 'text-zinc-500')}>Outlook</button>}
          {smtpConnected && <button onClick={() => setActiveProvider('smtp')} className={cn('flex-1 text-xs py-1.5 rounded-lg font-medium transition', provider === 'smtp' ? 'bg-[#5048e5] text-white' : 'text-zinc-500')}>{smtpStatus?.email?.split('@')[0]}</button>}
        </div>
      )}

      {/* Connected account + AI Classify */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-600">{provider === 'gmail' ? gmailStatus?.email : provider === 'outlook' ? outlookStatus?.email : smtpStatus?.email}</p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => classifyMutation.mutate(false)}
            disabled={classifyMutation.isPending}
            className="px-2.5 py-1 bg-[#5048e5]/10 border border-[#5048e5]/20 rounded-lg text-xs text-[#7c75f0] flex items-center gap-1 hover:bg-[#5048e5]/20 transition disabled:opacity-40"
          >
            {classifyMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />} Classify
          </button>
          {classifications.size > 0 && (
            <button
              onClick={() => classifyMutation.mutate(true)}
              disabled={classifyMutation.isPending}
              className="px-2.5 py-1 bg-white/[0.04] border border-white/[0.06] rounded-lg text-xs text-zinc-400 flex items-center gap-1 hover:bg-white/[0.08] transition disabled:opacity-40"
            >
              <RotateCcw className="h-3 w-3" /> Re-classify
            </button>
          )}
        </div>
      </div>

      {/* Category Filters (show when we have classifications) */}
      {classifications.size > 0 && (
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setCategoryFilter('all')} className={cn('px-2.5 py-1 rounded-lg text-[11px] font-medium transition', categoryFilter === 'all' ? 'bg-[#5048e5] text-white' : 'bg-white/[0.04] text-zinc-500')}>
            All
          </button>
          {Object.entries(CATEGORY_STYLES).map(([key, style]) => {
            const msgs = inbox?.messages || [];
            const count = msgs.filter((m: any) => classifications.get(m.id)?.category === key).length;
            if (count === 0) return null;
            const Icon = style.icon;
            return (
              <button key={key} onClick={() => setCategoryFilter(key)} className={cn('px-2.5 py-1 rounded-lg text-[11px] font-medium transition flex items-center gap-1', categoryFilter === key ? `${style.bg} ${style.color}` : 'bg-white/[0.04] text-zinc-500')}>
                <Icon className="h-3 w-3" /> {style.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Search (Gmail only supports full search) */}
      {provider === 'gmail' && (
        <input placeholder="Search emails..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#5048e5]/40" />
      )}

      {loadingInbox ? (
        <SkeletonEmailList />
      ) : (() => {
        // Merge classifications into inbox messages
        const allMessages = (inbox?.messages || []).map((m: any) => ({
          ...m,
          classification: classifications.get(m.id) || null,
        }));
        const displayMessages = categoryFilter === 'all'
          ? allMessages
          : allMessages.filter((m: any) => m.classification?.category === categoryFilter);

        return displayMessages.length === 0 ? (
          <div className="text-center py-16 text-zinc-600"><Inbox className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No emails found</p></div>
        ) : (
          <div className="space-y-1">
            {displayMessages.map((msg: any) => {
              const cat = msg.classification ? CATEGORY_STYLES[msg.classification.category] : null;
              const CatIcon = cat?.icon;
              return (
                <button
                  key={msg.id}
                  onClick={() => { haptic('light'); openMessage(msg); }}
                  className={cn('w-full text-left p-3 rounded-xl border transition hover:bg-white/[0.03] animate-[slideUp_0.2s_ease-out]', msg.isUnread ? 'bg-[#5048e5]/5 border-[#5048e5]/10' : 'bg-zinc-900/20 border-white/[0.04]')}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative shrink-0">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ backgroundColor: getAvatarColor(extractName(msg.from)) }}>
                        {getInitials(extractName(msg.from))}
                      </div>
                      {msg.isUnread && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#5048e5] border-2 border-[#0e0a1a]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className={cn('text-sm truncate', msg.isUnread ? 'font-semibold' : 'font-medium')}>{extractName(msg.from)}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {cat && CatIcon && (
                            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-md flex items-center gap-0.5', cat.bg, cat.color)}>
                              <CatIcon className="h-2.5 w-2.5" />{cat.label}
                            </span>
                          )}
                          <span className="text-[10px] text-zinc-600">{formatDate(msg.date)}</span>
                        </div>
                      </div>
                      <p className={cn('text-sm truncate', msg.isUnread ? 'text-zinc-200' : 'text-zinc-400')}>{msg.subject}</p>
                      {msg.classification?.summary ? (
                        <p className="text-xs text-zinc-500 truncate mt-0.5">{msg.classification.summary}</p>
                      ) : (
                        <p className="text-xs text-zinc-600 truncate mt-0.5">{msg.snippet}</p>
                      )}
                      {msg.classification?.clientMatch && (
                        <p className="text-[10px] text-blue-400 mt-0.5 flex items-center gap-0.5"><Users className="h-2.5 w-2.5" />{msg.classification.clientMatch}</p>
                      )}
                </div>
                {msg.hasAttachments && <Paperclip className="h-3 w-3 text-zinc-600 shrink-0 mt-1" />}
              </div>
            </button>
              );
            })}
          </div>
        );
      })()}

      {/* Load More */}
      {(inbox?.messages || []).length >= emailLimit && (
        <button
          onClick={() => setEmailLimit(prev => prev + 20)}
          className="w-full py-2.5 rounded-xl border border-white/[0.08] text-sm text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04] transition flex items-center justify-center gap-2"
        >
          <ChevronDown className="h-4 w-4" /> Load more emails
        </button>
      )}

      {/* Add another account button */}
      {hasAnyConnection && !(gmailConnected && smtpConnected) && (
        <button onClick={() => setView('connect')} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-dashed border-white/[0.08] text-xs text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.02] transition">
          <Plus className="h-3.5 w-3.5" /> Add another email account
        </button>
      )}
    </div>
  );
}

// ==================== Cloud Storage Connections Component ====================
function CloudStorageConnections() {
  const { data: cloudStatus } = useQuery({
    queryKey: ['cloud-storage-status'],
    queryFn: async () => { try { const r = await apiClient.get('/cloud/status'); return r.data.data; } catch { return { googleDrive: { connected: false }, oneDrive: { connected: false } }; } },
    retry: false,
  });

  const queryClient = useQueryClient();

  const handleGoogleDriveConnect = async () => {
    try { const r = await apiClient.get('/cloud/google-drive/connect'); if (r.data?.data?.authUrl) window.location.href = r.data.data.authUrl; } catch {}
  };

  const handleOneDriveConnect = async () => {
    try { const r = await apiClient.get('/cloud/onedrive/connect'); if (r.data?.data?.authUrl) window.location.href = r.data.data.authUrl; } catch {}
  };

  const gdDisconnect = useMutation({
    mutationFn: async () => { await apiClient.delete('/cloud/google-drive/disconnect'); },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cloud-storage-status'] }),
  });

  const odDisconnect = useMutation({
    mutationFn: async () => { await apiClient.delete('/cloud/onedrive/disconnect'); },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cloud-storage-status'] }),
  });

  return (
    <div className="space-y-2">
      {/* Google Drive */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg"><path d="M6.6 66.85L3.4 72.2 17.5 78l3.2-5.35z" fill="#0066DA"/><path d="M43.65 0L26.55 29.55l17.1 29.5h34.2l17.1-29.5L77.85 0z" fill="#00AC47" fillOpacity=".4"/><path d="M26.55 29.55L0 78h34.2l26.55-48.45z" fill="#EA4335" fillOpacity=".4"/><path d="M60.75 78h26.55L60.75 29.55H26.55z" fill="#00832D" fillOpacity=".4"/></svg>
          <span className="text-sm">Google Drive</span>
        </div>
        {cloudStatus?.googleDrive?.connected ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded">{cloudStatus.googleDrive.email}</span>
            <button onClick={() => gdDisconnect.mutate()} className="text-xs text-red-400 hover:text-red-300">Disconnect</button>
          </div>
        ) : (
          <button onClick={handleGoogleDriveConnect} className="px-3 py-1 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition">Connect</button>
        )}
      </div>

      {/* OneDrive */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 3.5C7.46 3.5 4.92 5.58 4.25 8.39A5.5 5.5 0 005.5 19h13a5 5 0 00.97-9.91A7 7 0 0010.5 3.5z" fill="#0078D4"/></svg>
          <span className="text-sm">OneDrive</span>
        </div>
        {cloudStatus?.oneDrive?.connected ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded">{cloudStatus.oneDrive.email}</span>
            <button onClick={() => odDisconnect.mutate()} className="text-xs text-red-400 hover:text-red-300">Disconnect</button>
          </div>
        ) : (
          <button onClick={handleOneDriveConnect} className="px-3 py-1 bg-[#5048e5] rounded-lg text-xs font-medium hover:bg-[#5f58e8] transition">Connect</button>
        )}
      </div>
    </div>
  );
}
